// @givmo/app-admin — the employer `/admin/company/security` surface, addressed by URL (AS-WEB7).
//
// AS-BE19's workforce session reset and AS-BE20's security audit export. Both landed after the
// committed OpenAPI snapshot was last refreshed, so there is no generated operation for either;
// `agent_docs/api-client.md` § *A whole ROUTE the snapshot lacks* is the sanctioned disposition, and
// `sso-config-transport.ts` beside it is the same shape for the SSO family.
//
// ⚠ A DIFFERENT PATH FAMILY AND A DIFFERENT GATE FROM THAT FILE, which is why it is not more of it.
// `/admin/company/settings/sso/*` all ride one ROUTER dependency; these two are `/admin/company/
// security/*` and are gated PER ROUTE, differently from each other:
//
//   • reset-sso-sessions → `EMPLOYER_SSO_ENABLED` alone. It is SSO machinery.
//   • audit-export       → `EMPLOYER_SSO_ENABLED` **OR** `EMPLOYER_AUTH_POLICY_ENABLED`. The trail
//     spans BOTH employer security ladders, which are independently operator-flippable, so gating it
//     on the SSO flag alone would 404 an employer's audit trail in exactly the state the rollout
//     produces first — the MFA ladder live, SSO still dark — while rows were genuinely being written.
//
// So the two surfaces reveal on DIFFERENT verdicts, and neither needs a probe of its own: the tab
// already holds both (`settings-security-tab.tsx`). The reset renders with the SSO section; the
// export renders whenever the tab does.
//
// ══ THE EXPORT IS A BINARY DOWNLOAD, NOT A JSON READ ══════════════════════════════════════════
// It answers `text/csv` with the artifact in the body, so it rides this app's existing
// `binaryDownloadOptions` (`features/reports/csv-export.ts`, which that module declares as the admin
// app's ONE home for download plumbing): `parseAs:'blob'` so the body is not run through JSON
// parsing, `responseStyle:'fields'` so the caller gets the raw `Response`. The tax-document download
// already goes through the same pair.
//
// ⚠ THE RAW `Response` IS LOAD-BEARING HERE, not just for a filename. This endpoint reports the
// window it ACTUALLY served in response headers, and a clamped start is only visible there.

import { givmoClient } from '@givmo/api-client';
import { asSaveableBlob, binaryDownloadOptions } from '../reports/csv-export';

export const COMPANY_SECURITY_URL = '/admin/company/security';
export const RESET_SSO_SESSIONS_URL = `${COMPANY_SECURITY_URL}/reset-sso-sessions`;
export const AUDIT_EXPORT_URL = `${COMPANY_SECURITY_URL}/audit-export`;

/**
 * End every live session across the employer's roster.
 *
 * TAKES NO BODY, and the absence is the design: the act has no parameters, and giving it one (a
 * subset of employees, an "except me") would turn a blunt incident control into a targeting tool.
 * A POST rather than a DELETE because nothing is removed — the sessions expire, the accounts do not.
 *
 * ⚠ NOT IDEMPOTENT IN THE WAY A RETRY CARES ABOUT — it is SAFELY repeatable. Revocation is
 * idempotent per session, so running it twice signs the same people out twice rather than doing
 * anything new, which is precisely why a non-zero `failed` count is actionable: the remedy is to
 * press it again.
 */
export async function resetSsoSessions(): Promise<unknown> {
  const { data } = await givmoClient.post({ url: RESET_SSO_SESSIONS_URL, throwOnError: true });
  return data;
}

/**
 * AS-BE96 (D-AS-70) — reset ONE employee's company sign-in: the backend clears their binding and keeps
 * the employment and its directory pairing, so the person connects their work account again
 * themselves. Served by the same backend router as the two routes above and gated like the reset
 * (`EMPLOYER_SSO_ENABLED`), under the employee's own path (`/admin/company/employees/{id}/…`), and
 * newer than the committed snapshot, so it is addressed by URL like them.
 *
 * Owner-only on the server (`company:edit`); an employee not on this company's roster is its 404
 * (`EMPLOYEE_NOT_ON_ROSTER`). TAKES NO BODY. Answers `{ released: boolean, email_sent: boolean }`:
 * `released` is `false` when the employee had no company sign-in to reset, which changed, recorded
 * and sent nothing, and `email_sent` says whether the person's notice went out (review-80 F4).
 */
export function ssoIdentityReleaseUrl(employeeUserId: string): string {
  return `/admin/company/employees/${encodeURIComponent(employeeUserId)}/sso-identity/release`;
}

export async function releaseEmployeeSsoIdentity(employeeUserId: string): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: ssoIdentityReleaseUrl(employeeUserId),
    throwOnError: true,
  });
  return data;
}

/** An inclusive UTC calendar day, `YYYY-MM-DD`, as the endpoint's `from`/`to` params spell one. */
export type AuditExportDay = string;

export interface AuditExportRequest {
  /** First UTC calendar day, inclusive. Omitted = the whole retained window. */
  from?: AuditExportDay;
  /** Last UTC calendar day, inclusive. Omitted = now. */
  to?: AuditExportDay;
}

/** What the download produced: the artifact, plus the raw `Response` its headers live on. */
export interface AuditExportResult {
  blob: Blob;
  response: Response;
}

/**
 * Fetch the security audit CSV.
 *
 * ⚠ AN OMITTED BOUND IS NOT THE SAME AS AN EMPTY ONE. A present-and-empty query value is serialized
 * and would mean a narrower request than leaving the key off; the builder therefore includes a key
 * only when the caller supplied a day, which is the screen's decision to make rather than the
 * client's (`agent_docs/api-client.md` § *Graduating an escape hatch*).
 *
 * ⚠ THE ERROR PATH IS WHY THIS DOES NOT USE `throwOnError`. The typed refusals — an inverted range,
 * and a window over the row cap — arrive as ordinary `{errors:[…]}` JSON on a 400 while the request
 * asked for a blob, so the caller is handed the mapped `error` and decides. Callers must check
 * `error` first: on a refusal there is no artifact, and saving the error body as a `.csv` would
 * hand an Owner a file containing a JSON error and call it an audit trail.
 */
export async function fetchAuditExport(request: AuditExportRequest = {}): Promise<{
  result: AuditExportResult | null;
  error: unknown;
}> {
  const query: Record<string, string> = {};
  if (request.from !== undefined) query.from = request.from;
  if (request.to !== undefined) query.to = request.to;

  const { data, error, response } = await givmoClient.get({
    url: AUDIT_EXPORT_URL,
    ...(Object.keys(query).length > 0 ? { query } : {}),
    ...binaryDownloadOptions,
  });

  if (error !== undefined && error !== null) return { result: null, error };
  if (response === undefined) {
    // No response at all is a real failure, surfaced rather than saved, so a zero-byte download
    // never reaches the Owner looking like an export.
    return { result: null, error: new Error('The export did not return a file.') };
  }
  // ⚠ NOT `data instanceof Blob` — see `asSaveableBlob`, which exists because that operator answers
  // FALSE under test on a value that is genuinely a Blob, and because the obvious repair silently
  // saves a file containing the literal text `[object Blob]`.
  return { result: { blob: asSaveableBlob(data, 'text/csv'), response }, error: null };
}
